import axios from "axios";
import { creditAPI } from "./creditApi";

// Environment-aware base URL
const API_URL =
  import.meta.env.VITE_CRM_API_URL || "https://dashboard.lono.co.in/api/crm";

// Axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add Authorization token via interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Valid statuses for leads
const allowedStatuses = [
  "new-leads",
  "in-review",
  "approved",
  "rejected",
  "processing",
  "submitted",
];

// Helper for GET requests by status
const fetchLeadsByStatus = async (url, status, params, bodyKey, id) => {
  if (!allowedStatuses.includes(status)) {
    throw new Error(`Invalid status: ${status}`);
  }
  return (
    await api.get(url, {
      params,
      ...(id && { data: { [bodyKey]: id } }), // only attach body if id exists
    })
  ).data;
};

export const crmAPI = {
  // General
  getAllowedStatuses: () => [...allowedStatuses],
  viewLeadDetailsRoute: (leadId) => `/customers/leads/${leadId}`,

  // Leads by Status
  getLeadsByStatus: (status, page = 1, limit = 10) =>
    fetchLeadsByStatus(`/leads/status/${status}`, status, { page, limit }),

  getLeadsByStatusForStaff: (status, staffId, page = 1, limit = 10) =>
    fetchLeadsByStatus(
      `/staff/leads/status/${status}`,
      status,
      { page, limit },
      "staffId",
      staffId
    ),

  getLeadsByStatusForCreditManager: (status, staffId, page = 1, limit = 10) =>
    fetchLeadsByStatus(
      `/cm/leads/status/${status}`,
      status,
      { page, limit },
      "staffId",
      staffId
    ),

  getLeadsByStatusForBankingPartner: (status, id, page = 1, limit = 10) =>
    fetchLeadsByStatus(
      `/bp/leads/status/${status}`,
      status,
      { page, limit },
      "id",
      id
    ),

  // Single lead and staff-lead APIs
  getLeadById: async (loanId) => (await api.get(`/loan/${loanId}`)).data,
  getLeadsByStaff: async (staffId) =>
    (await api.get(`/leads/staff/${staffId}`)).data,

  // Document APIs
  getLeadDocuments: async (leadId) =>
    (await api.get(`/leads/${leadId}/documents`)).data,

  verifyDocument: async (leadId, documentId, status) =>
    (
      await api.patch(`/leads/${leadId}/documents/${documentId}/verify`, {
        status,
      })
    ).data,

  requestDocuments: async (leadId, message) =>
    (await api.post(`/leads/${leadId}/request-documents`, { message })).data,

  downloadLeadDocument: async (documentId) => {
    try {
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      const contentDisposition = response.headers["content-disposition"];
      let filename = documentId;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match && match[1]) filename = match[1].replace(/['"]/g, "");
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        throw JSON.parse(text);
      }
      throw error.response?.data || error.message;
    }
  },

  // Loan actions
  updateLoanStatus: async (loanId, status) =>
    (await api.patch(`/loan/${loanId}/update/status`, { status })).data,

  assignLeadToStaff: async (loanId, assignedTo) =>
    (await api.patch(`/loan/${loanId}/assign-lead`, { assignedTo })).data,

  distributeLoanAndUpdateAmount: async (loanId, distributionData) =>
    (await api.post(`/loan/${loanId}/distribute-update`, distributionData))
      .data,

  approveFinalLoanDistribution: async (distributionId) =>
    (await api.patch(`/loan-distributions/${distributionId}/cm-select`)).data,

  selectFinalBankingPartner: async (loanId, distributionId) => {
    try {
      return await creditAPI.selectFinalBankingPartner(loanId, distributionId);
    } catch (error) {
      throw error;
    }
  },

  // Generate and download loan application PDF
  generateLoanApplicationPDF: async (loanId) => {
    try {
      const response = await api.get(`/loan/${loanId}/generate-pdf`, {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      // Extract filename from headers or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = `loan_application_${loanId}.pdf`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, "");
        }
      }

      // Create download link
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      return { success: true, filename };
    } catch (error) {
      console.error("Error generating PDF:", error);
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        throw JSON.parse(text);
      }
      throw error.response?.data || error.message;
    }
  },
};
